import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.highlight ? 'white' : props.theme.colors.primary};
`;

const NoticeBanner = styled.div`
  background: rgba(16, 29, 64, 0.06);
  border-left: 4px solid ${props => props.theme.colors.accent};
  border-radius: 0 10px 10px 0;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.selected ? '600' : '500'};
  opacity: ${props => props.selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success'
    ? '#d4edda'
    : props.type === 'error'
    ? '#f8d7da'
    : '#d1ecf1'};
  color: ${props => props.type === 'success'
    ? '#155724'
    : props.type === 'error'
    ? '#721c24'
    : '#0c5460'};
  border: 1px solid ${props => props.type === 'success'
    ? '#c3e6cb'
    : props.type === 'error'
    ? '#f5c6cb'
    : '#bee5eb'};
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 2rem 0;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.6;
  margin: 0;
`;

const SocialMediaAssistant = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverNote: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!cvFile) {
      setStatus({ type: 'error', message: 'Please attach your CV.' });
      return;
    }
    if (!coverFile) {
      setStatus({ type: 'error', message: 'Please attach your cover letter.' });
      return;
    }
    if (!form.coverNote.trim()) {
      setStatus({ type: 'error', message: 'Please include a cover note.' });
      return;
    }

    setSubmitting(true);
    try {
      const [cvData, coverData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: 'social-media-assistant',
        roleTitle: 'Social Media Assistant',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        coverNote: form.coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData: coverData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Social Media Assistant"
        subtitle="Marketing · Belvoir Castle"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">← Back to Careers</BackLink>

        <JobHeader>
          <JobTitle>Social Media Assistant</JobTitle>
          <JobSubtitle>Belvoir Castle, Leicestershire</JobSubtitle>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>In person — Belvoir Castle, Leicestershire</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Department</MetaLabel>
              <MetaValue>Marketing</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Focus</MetaLabel>
              <MetaValue>Social media &amp; content creation</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Eligibility</MetaLabel>
              <MetaValue>UK applications only</MetaValue>
            </MetaItem>
          </MetaRow>
          <TagRow>
            <Tag highlight>Now Hiring</Tag>
            <Tag highlight>UK Applications Only</Tag>
            <Tag>Marketing</Tag>
            <Tag>Social Media</Tag>
            <Tag>On-site</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          <div>
            <Section>
              <SectionTitle>Role Overview</SectionTitle>
              <NoticeBanner>
                UK applications only. We are unable to consider applications from outside the United Kingdom.
              </NoticeBanner>
              <BodyText>
                Belvoir Castle is seeking a creative, motivated and highly organised Social Media Assistant to
                join our marketing team and support the continued growth of the Belvoir Castle brand across
                digital channels.
              </BodyText>
              <BodyText>
                This is an excellent opportunity for a recent graduate or someone at the beginning of their
                marketing career who is looking to gain hands-on experience within a unique heritage, tourism
                and events environment.
              </BodyText>
              <BodyText>
                The role will initially focus on social media management and content creation across Instagram,
                Facebook, TikTok and X, including photography, video, short-form content, copywriting and
                community engagement.
              </BodyText>
              <BodyText>
                As the role develops, there will be significant opportunity to gain experience across the wider
                digital marketing mix, including email marketing, Meta and Google advertising, SEO, website
                content, analytics, audience targeting and campaign performance.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Key Responsibilities</SectionTitle>
              <BulletList>
                <li>Planning, creating and scheduling social media content.</li>
                <li>Producing photography, video, Reels and short-form content.</li>
                <li>Writing engaging captions and marketing copy.</li>
                <li>Capturing content across Belvoir Castle, its events and the wider Estate.</li>
                <li>Monitoring comments, messages and community engagement.</li>
                <li>Keeping up to date with social media trends and emerging formats.</li>
                <li>Monitoring reach, engagement and audience growth.</li>
                <li>Supporting major seasonal campaigns and events.</li>
                <li>Assisting with email marketing and newsletters.</li>
                <li>Supporting paid digital advertising campaigns.</li>
                <li>Developing knowledge of SEO, website content and digital analytics.</li>
                <li>Assisting with campaign reporting and performance analysis.</li>
                <li>Supporting the wider marketing team with administrative and campaign activity.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>Skills &amp; Experience</SectionTitle>
              <BodyText>
                We are particularly interested in candidates with a degree in Digital Marketing, Marketing,
                Communications, Media or a related subject, although equivalent relevant experience will also
                be considered.
              </BodyText>
              <BodyText>Candidates should have:</BodyText>
              <BulletList>
                <li>A strong understanding of major social media platforms.</li>
                <li>Excellent written communication skills.</li>
                <li>Good visual awareness and an interest in photography and video.</li>
                <li>Creativity and confidence generating new ideas.</li>
                <li>Strong organisational skills and attention to detail.</li>
                <li>An enthusiasm for developing a career in social media and digital marketing.</li>
                <li>The ability to work independently and as part of a team.</li>
              </BulletList>
              <BodyText>
                Experience with Canva, Adobe Premiere Pro, Meta Business Suite, TikTok Analytics, Google
                Analytics or similar tools would be advantageous but is not essential.
              </BodyText>
              <BodyText>
                This role offers genuine development opportunities. While social media and content creation will
                form the core of the position initially, the successful candidate will be encouraged to broaden
                their experience across paid advertising, email marketing, SEO, website management, analytics
                and wider digital campaigns.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Benefits</SectionTitle>
              <BulletList>
                <li>Professional development and training opportunities.</li>
                <li>Company pension.</li>
                <li>Employee discount.</li>
                <li>Free on-site parking.</li>
                <li>Hands-on involvement in major events and seasonal campaigns.</li>
                <li>A unique working environment within one of Britain&apos;s most historic privately owned estates.</li>
              </BulletList>
            </Section>
          </div>

          <StickyCard>
            {submitted ? (
              <SuccessWrap>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessTitle>Application Received</SuccessTitle>
                <SuccessText>
                  Thank you for your application. We&apos;ll be in touch if your experience matches what we&apos;re looking for.
                </SuccessText>
              </SuccessWrap>
            ) : (
              <>
                <CardTitle>Apply for This Role</CardTitle>
                <NoticeBanner style={{ marginBottom: '1.25rem' }}>
                  UK applications only
                </NoticeBanner>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>First Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Jane"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Last Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Email Address <span className="req">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@example.com"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+44 7700 900000"
                    />
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>CV <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!cvFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCvFile)}
                      />
                      <FileUploadText selected={!!cvFile}>
                        {cvFile ? cvFile.name : 'Choose file — CV'}
                      </FileUploadText>
                      {!cvFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <FormGroup>
                    <Label>Cover Letter <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!coverFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCoverFile)}
                      />
                      <FileUploadText selected={!!coverFile}>
                        {coverFile ? coverFile.name : 'Choose file — Cover Letter'}
                      </FileUploadText>
                      {!coverFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>Cover Note <span className="req">*</span></Label>
                    <TextArea
                      name="coverNote"
                      value={form.coverNote}
                      onChange={handleChange}
                      placeholder="Tell us about your interest in social media and digital marketing, why you'd like to join Belvoir Castle, and anything else that strengthens your application."
                      rows={5}
                      required
                    />
                  </FormGroup>

                  {status && (
                    <StatusMessage type={status.type}>{status.message}</StatusMessage>
                  )}

                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </SubmitButton>

                  <SmallNote style={{ marginTop: '0.85rem' }}>
                    UK applications only. Your information is kept confidential and used only for this application.
                  </SmallNote>
                </form>
              </>
            )}
          </StickyCard>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default SocialMediaAssistant;
